import { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Button } from "react-bootstrap";

const MapSelectionOverlayInternal = ({
  map,
  onSelectionComplete,
  onDisable,
}) => {
  const mapContainer = map.getContainer();
  const [selectorStyle, setSelectorStyle] = useState({});
  const [showPitchWarning, setShowPitchWarning] = useState(false);

  const selectorRef = useRef(null);
  const resizingRef = useRef(false);
  const activeHandleRef = useRef(null);
  const startDragPtRef = useRef(null);
  const initialBoundsRef = useRef(null);

  // --- イベントハンドラ ---
  const handleConfirm = useCallback(() => {
    if (!selectorRef.current || !onSelectionComplete) return;

    const dpr = window.devicePixelRatio || 1;
    const selectorRect = selectorRef.current.getBoundingClientRect();
    const containerRect = mapContainer.getBoundingClientRect();

    const wCss = selectorRect.width;
    const hCss = selectorRect.height;
    const wDev = Math.round(wCss * dpr);
    const hDev = Math.round(hCss * dpr);

    const centerX = selectorRect.left + wCss / 2 - containerRect.left;
    const centerY = selectorRect.top + hCss / 2 - containerRect.top;

    const selectionCenter = map.unproject([centerX, centerY]);

    const selectionData = {
      center: map.getCenter(), // 元の地図中心
      selectionCenter: selectionCenter, // 選択範囲の中心座標
      sizeCssPx: { width: wCss, height: hCss },
      sizeDevicePx: { width: wDev, height: hDev },
      dpr,
    };

    onSelectionComplete(selectionData);
    onDisable();
  }, [map, mapContainer, onSelectionComplete, onDisable]);

  const handlePointerMove = useCallback(
    (e) => {
      if (!resizingRef.current) return;
      e.preventDefault();

      const dx = e.clientX - startDragPtRef.current.x;
      const dy = e.clientY - startDragPtRef.current.y;

      const { left, top, width, height } = initialBoundsRef.current;
      const mapWidth = mapContainer.clientWidth;
      const mapHeight = mapContainer.clientHeight;

      let dw = 0;
      let dh = 0;

      if (activeHandleRef.current.includes("right")) dw = dx;
      else if (activeHandleRef.current.includes("left")) dw = -dx;

      if (activeHandleRef.current.includes("bottom")) dh = dy;
      else if (activeHandleRef.current.includes("top")) dh = -dy;

      const maxDw = Math.min(left, mapWidth - (left + width));
      const maxDh = Math.min(top, mapHeight - (top + height));

      if (dw > maxDw) dw = maxDw;
      if (dh > maxDh) dh = maxDh;

      let newWidth = width + dw * 2;
      let newHeight = height + dh * 2;
      let newLeft = left - dw;
      let newTop = top - dh;

      // 確定ボタンを基準に最小サイズを確保
      const minWidth = 150; // button.offsetWidth + 20
      const minHeight = 60; // button.offsetHeight + 20

      if (newWidth < minWidth) {
        newLeft += (newWidth - minWidth) / 2;
        newWidth = minWidth;
      }
      if (newHeight < minHeight) {
        newTop += (newHeight - minHeight) / 2;
        newHeight = minHeight;
      }

      setSelectorStyle((prev) => ({
        ...prev,
        width: `${newWidth}px`,
        height: `${newHeight}px`,
        left: `${newLeft}px`,
        top: `${newTop}px`,
      }));
    },
    [mapContainer]
  );

  const handlePointerUp = useCallback(() => {
    resizingRef.current = false;
    activeHandleRef.current = null;
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
  }, [handlePointerMove]);

  const handlePointerDown = useCallback(
    (e) => {
      if (e.target.dataset.handle) {
        e.preventDefault();
        e.stopPropagation();

        resizingRef.current = true;
        activeHandleRef.current = e.target.dataset.handle;
        startDragPtRef.current = { x: e.clientX, y: e.clientY };

        const selectorRect = selectorRef.current.getBoundingClientRect();
        const containerRect = mapContainer.getBoundingClientRect();

        initialBoundsRef.current = {
          left: selectorRect.left - containerRect.left,
          top: selectorRect.top - containerRect.top,
          width: selectorRect.width,
          height: selectorRect.height,
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
      }
    },
    [mapContainer, handlePointerMove, handlePointerUp]
  );

  // --- 初期化とイベントリスナーの設定 ---
  useEffect(() => {
    // 1. セレクターの初期化 (resetSelector)
    const mapWidth = mapContainer.clientWidth;
    const mapHeight = mapContainer.clientHeight;
    const initialSize = Math.min(mapWidth, mapHeight) * 0.5;
    const left = (mapWidth - initialSize) / 2;
    const top = (mapHeight - initialSize) / 2;

    setSelectorStyle({
      position: "absolute",
      border: "2px dashed #333",
      backgroundColor: "rgba(255, 255, 255, 0.4)",
      boxSizing: "border-box",
      left: `${left}px`,
      top: `${top}px`,
      width: `${initialSize}px`,
      height: `${initialSize}px`,
    });

    // 2. 地図の傾き監視 (updatePitchWarning)
    const updatePitchWarning = () => {
      const pitch = map.getPitch();
      setShowPitchWarning(pitch !== 0);
    };
    updatePitchWarning(); // 初期チェック
    map.on("pitch", updatePitchWarning);

    // 3. Escapeキーでのキャンセル処理
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onDisable();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    // クリーンアップ関数
    return () => {
      map.off("pitch", updatePitchWarning);
      window.removeEventListener("keydown", handleKeyDown);
      // 安全のため、アンマウント時にリサイズリスナーも削除
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [map, mapContainer, onDisable, handlePointerMove, handlePointerUp]);

  const handleStyle = {
    position: "absolute",
    width: "18px",
    height: "18px",
    backgroundColor: "#333",
    border: "2px solid #fff",
    borderRadius: "3px",
    zIndex: 20,
    touchAction: "none",
  };

  const handles = [
    { position: "top-left", cursor: "nwse-resize" },
    { position: "top-right", cursor: "nesw-resize" },
    { position: "bottom-left", cursor: "nesw-resize" },
    { position: "bottom-right", cursor: "nwse-resize" },
  ];

  const getHandleStyle = (position) => {
    const offset = -11; // (18/2 + 2)
    const style = { ...handleStyle, cursor: position.cursor };
    if (position.position.includes("top")) style.top = `${offset}px`;
    if (position.position.includes("bottom")) style.bottom = `${offset}px`;
    if (position.position.includes("left")) style.left = `${offset}px`;
    if (position.position.includes("right")) style.right = `${offset}px`;
    return style;
  };

  return ReactDOM.createPortal(
    <div
      className="map-selection-overlay"
      onPointerDown={handlePointerDown}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 10,
        pointerEvents: "auto",
      }}
    >
      <div ref={selectorRef} style={selectorStyle}>
        {handles.map((h) => (
          <div
            key={h.position}
            data-handle={h.position}
            style={getHandleStyle(h)}
          />
        ))}
        <Button
          variant="primary"
          size="sm"
          onClick={handleConfirm}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 30,
            whiteSpace: "nowrap",
          }}
        >
          画像ダウンロード
        </Button>
        <div
          style={{
            position: "absolute",
            top: "calc(50% + 25px)",
            left: "50%",
            transform: "translateX(-50%)",
            color: "black",
            fontSize: "14px",
            whiteSpace: "nowrap",
            zIndex: 30,
            backgroundColor: "rgba(255, 255, 255, 1)",
          }}
        >
          枠の範囲で画像を作成します
        </div>
        {showPitchWarning && (
          <div
            style={{
              position: "absolute",
              top: "calc(50% + 45px)",
              left: "50%",
              transform: "translateX(-50%)",
              color: "black",
              fontSize: "14px",
              whiteSpace: "nowrap",
              zIndex: 30,
              backgroundColor: "rgba(255, 255, 255, 1)",
            }}
          >
            地図の傾きや範囲が変わる可能性があります
          </div>
        )}
        <Button
          variant="link"
          size="sm"
          onClick={onDisable}
          style={{
            position: "absolute",
            top: showPitchWarning ? "calc(50% + 65px)" : "calc(50% + 45px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 30,
            whiteSpace: "nowrap",
            backgroundColor: "rgba(255, 255, 255, 1)",
          }}
          className="mt-2"
        >
          キャンセル
        </Button>
      </div>
    </div>,
    mapContainer
  );
};

const MapSelectionOverlay = (props) => {
  if (!props.map || !props.isActive) {
    return null;
  }
  return <MapSelectionOverlayInternal {...props} />;
};

export default MapSelectionOverlay;
